import React, { useContext, createContext } from 'react';
/* eslint-disable react/no-array-index-key */
import Button from '@teetsh/app/src/components/Buttons/Button';
import { PlusCircleIcon as PlusCircle } from '@heroicons/react/24/outline';
import { TrashIcon as Trash } from '@heroicons/react/24/outline';
import tw from 'twin.macro';
import DeleteButton from 'components/Buttons/DeleteButton';
import { useTranslation } from 'react-i18next';

export interface Props<T> {
  items: T[];
  itemKeyFn?: (item: T) => string;
  inline?: boolean;
  onRemove: (index: number) => void;
  onAdd: () => void;
  children: React.ReactNode;
}

const DynamicListContext = createContext<
  | {
      index: number;
      item: any;
    }
  | undefined
>(undefined);

export function useDynamicListContext() {
  return useContext(DynamicListContext);
}

export default function DynamicList<T>(props: Props<T>) {
  const { items, itemKeyFn, onAdd, onRemove, children, inline } = props;
  const { t } = useTranslation(['common']);
  return (
    <div
      css={[
        tw`grid gap-3`,
        !inline && tw`grid-cols-12`,
        inline && tw`grid-cols-1`,
      ]}
    >
      {items.map((item, index) => (
        <div
          key={itemKeyFn ? itemKeyFn(item) : index}
          tw="col-span-full md:col-span-6"
        >
          <div tw="flex items-center">
            <div tw="flex-grow">
              <DynamicListContext.Provider value={{ index, item }}>
                {children}
              </DynamicListContext.Provider>
            </div>
            <div tw="ml-1">
              <DeleteButton
                onClick={() => onRemove(index)}
                withConfirmation
                popoverText={
                  <div>
                    <div tw="mb-2 font-bold">
                      {t('common:components.dynamicList.deleteQuestion')}
                    </div>
                    <p tw="text-sm">
                      {t('common:components.dynamicList.deleteDescription')}
                    </p>
                  </div>
                }
                minimal
                icon={Trash}
              />
            </div>
          </div>
        </div>
      ))}
      <div tw="col-span-full md:col-span-6">
        <Button onClick={() => onAdd()} minimal icon={PlusCircle}>
          {t('common:buttons.addItem')}
        </Button>
      </div>
    </div>
  );
}
